import React, { useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import "./privacy.css";

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // In the app this page carries the fixed header + tab bar (Profile tab lit),
  // so it owns its scroll — see .legal-scroll in privacy.css. Same test the
  // tab bar itself uses; on logged-out web the wrapper is class-less and the
  // document scrolls past the footer exactly as before.
  const { user, isAuthenticated } = useContext(AuthContext);
  const inApp = isAuthenticated && !!user;
  // Back to wherever they came from within the app. If they landed here directly
  // (external/deep link — router key is "default"), go to the home page instead
  // of navigate(-1), which would eject them OUT of the app. (These are public
  // pages, also reachable from the footer / SEO / App Store.)
  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/");
  };
  useEffect(() => {
    // Reset scroll so you land at the top (with the back arrow) — navigation
    // otherwise keeps the previous page's scroll (same fix as LeaderboardPage).
    window.scrollTo(0, 0);
    document.title = "Privacy Policy — Request App";
  }, []);

  return (
    <div className={inApp ? "legal-scroll" : undefined}>
    <main className="rqpv-wrap" role="main">
      <div className="rqpv-card">
        <button type="button" className="rqpv-back" onClick={goBack} aria-label="Back" title="Back">
          <FaArrowLeft size={16} />
        </button>
        <header className="rqpv-header">
          <div className="rqpv-title">Privacy Policy</div>
          <div className="rqpv-meta">
            <span>Effective Date: November 3, 2026</span>
            <span>Company: REQUESTA LLC (Wyoming, USA)</span>
            <span>Contact: <a className="rqpv-link" href="mailto:hello@requesta.app">hello@requesta.app</a></span>
          </div>
        </header>


        <section id="rqpv-controller" className="rqpv-section">
          <div className="rqpv-h2">1. Data Controller</div>
          <div className="rqpv-text">
            REQUESTA LLC is the data controller responsible for your personal data.
            We can be contacted at <a className="rqpv-link" href="mailto:hello@requesta.app">hello@requesta.app</a>.
          </div>
          <div className="rqpv-text" style={{ marginTop: "10px" }}>
            We use trusted service providers as data processors, including:
          </div>
          <ul className="rqpv-list">
            <li className="rqpv-li">Stripe, Inc. (payment processing)</li>
            <li className="rqpv-li">Resend (transactional email)</li>
            <li className="rqpv-li">Google (venue search)</li>
            <li className="rqpv-li">Railway, Vercel, MongoDB Atlas (hosting and infrastructure)</li>
          </ul>
        </section>

        <section id="rqpv-info" className="rqpv-section">
          <div className="rqpv-h2">2. Information We Collect</div>
          <div className="rqpv-text">We collect only what is necessary to provide the App:</div>
          <ul className="rqpv-list">
            <li className="rqpv-li"><strong>Contact Information:</strong> Name, email address, phone number (when voluntarily provided).</li>
            <li className="rqpv-li"><strong>Identifiers:</strong> Account login credentials (hashed), device identifiers.</li>
            <li className="rqpv-li"><strong>Financial Information:</strong> Payment details processed via Stripe, Apple Pay, and Link (we do not store card data).</li>
            <li className="rqpv-li"><strong>Profile Content:</strong> Profile photo and profile details you choose to add (artist name, bio, venue, Instagram handle).</li>
            <li className="rqpv-li"><strong>Instagram verification (optional):</strong> If you tap “Verify” and sign in with Instagram, Instagram (Meta) shares your Instagram account ID, username, account type and follower count with us. We use them only to confirm that you control the account, to show a “Verified” badge on your profile, and to help our team review your profile. We never receive your Instagram password, we do not store Instagram access tokens, and we do not read your posts or messages. Clearing or changing your Instagram handle deletes this data, and so does deleting your account. You can also remove Request App’s access in Instagram under Settings, then Website permissions, then Apps and websites.</li>
            <li className="rqpv-li"><strong>Instagram verification by message (optional):</strong> If you tap “Verify” and send a message to our Instagram account (@request_app_official), Instagram (Meta) tells us which Instagram account sent it. We then read that account’s username, follower count and whether Instagram has verified it, and keep them together with the ID Instagram gives that account for our inbox. We use them only to confirm that the handle is yours, to show a verified tick next to it in the App, and to help our team review talent profiles. Your message stays in our Instagram inbox like any other message sent to us; we do not read your other messages or your posts. Changing or clearing your Instagram handle in the App deletes this data, and so does deleting your account.</li>
            <li className="rqpv-li"><strong>Venue search:</strong> When you search for the venue where you perform, what you type is sent to Google to find matching places. If you pick a place, we keep its name and Google’s ID for that place on your profile. Google’s address lines are shown to you, not stored.</li>
            <li className="rqpv-li"><strong>Country:</strong> We estimate your country from your connection or your bank country, and you can change it in Edit Profile.</li>
            <li className="rqpv-li"><strong>Usage Data:</strong> Song requests, in-app messages, payment history, and how you use the app (for example which screens you open and which signup steps you complete), which we use to understand and improve the product.</li>
            <li className="rqpv-li"><strong>Diagnostics:</strong> Limited technical logs for stability, security, and performance.</li>
          </ul>
          <div className="rqpv-text" style={{ marginTop: "10px" }}>
            We do not collect health data, biometric data, or data for advertising tracking. If you allow it, the App uses your approximate location only to show venues near you when you search for your venue; it is sent to Google for that search and never stored.
          </div>
        </section>

        <section id="rqpv-purposes" className="rqpv-section">
          <div className="rqpv-h2">3. Purposes of Processing</div>
          <div className="rqpv-text">We process personal data for the following purposes:</div>
          <ul className="rqpv-list">
            <li className="rqpv-li">App Functionality (enabling requests, account features).</li>
            <li className="rqpv-li">Account Management (registration, login, profile).</li>
            <li className="rqpv-li">Payments and Payouts (via Stripe, Apple Pay, and Link).</li>
            <li className="rqpv-li">App Improvement (stability, performance, and bug fixes).</li>
            <li className="rqpv-li">Tips Emails: we also email talent occasional tips about using Request App, based on the choice they made at sign-up or on what the law in their country allows. Every tips email has an Unsubscribe link, and unsubscribing never stops account, payout or request emails.</li>
          </ul>
        </section>

        <section id="rqpv-share" className="rqpv-section">
          <div className="rqpv-h2">4. Data Sharing</div>
          <div className="rqpv-text">We share data only with trusted processors necessary to operate the App:</div>
          <ul className="rqpv-list">
            <li className="rqpv-li">Stripe for payments.</li>
            <li className="rqpv-li">Resend for transactional email.</li>
            <li className="rqpv-li">Google for venue search (what you type there and, if you allow it, your approximate location).</li>
            <li className="rqpv-li">Railway, Vercel, MongoDB Atlas for hosting and technical services.</li>
          </ul>
          <div className="rqpv-text" style={{ marginTop: "10px" }}>
            If you have a fan account, other people on Request App can see your profile card: your name, photo, Instagram if you added it, country, when you joined, and your request, played and points totals.
          </div>
          <div className="rqpv-text" style={{ marginTop: "10px" }}>
            We do not sell, rent, or share personal data with advertisers. We do not use data for cross-app tracking.
          </div>
        </section>

        <section id="rqpv-consent" className="rqpv-section">
          <div className="rqpv-h2">5. Consent &amp; User Controls</div>
          <ul className="rqpv-list">
            <li className="rqpv-li">You may delete your account at any time from within the App or by contacting <a className="rqpv-link" href="mailto:hello@requesta.app">hello@requesta.app</a>.</li>
          </ul>
        </section>

        <section id="rqpv-retention" className="rqpv-section">
          <div className="rqpv-h2">6. Data Retention</div>
          <ul className="rqpv-list">
            <li className="rqpv-li"><strong>Account data:</strong> Stored as long as your account is active. When you request deletion, your account is removed after a 7-day grace period during which you can change your mind.</li>
            <li className="rqpv-li"><strong>Payment data:</strong> Retained by Stripe in accordance with financial regulations.</li>
            <li className="rqpv-li"><strong>Transaction records:</strong> Payout and dispute records are retained as required for financial compliance and fraud prevention, even after account deletion.</li>
            <li className="rqpv-li"><strong>Diagnostics/crash logs:</strong> Retained for a limited period necessary for stability improvements.</li>
          </ul>
        </section>

        <section id="rqpv-security" className="rqpv-section">
          <div className="rqpv-h2">7. Security &amp; International Transfers</div>
          <div className="rqpv-text">
            Data is encrypted in transit (TLS/HTTPS) and stored securely on cloud servers.
            Access is restricted to authorized personnel only.
            Data may be processed internationally (e.g., U.S.-based servers).
          </div>
        </section>

        <section id="rqpv-rights" className="rqpv-section">
          <div className="rqpv-h2">8. Your Rights</div>
          <div className="rqpv-text">You have the right to:</div>
          <ul className="rqpv-list">
            <li className="rqpv-li">Access your data.</li>
            <li className="rqpv-li">Correct inaccurate data.</li>
            <li className="rqpv-li">Request deletion of your account and personal data.</li>
            <li className="rqpv-li">Request a copy of your data in portable format.</li>
            <li className="rqpv-li">Contact us with complaints at <a className="rqpv-link" href="mailto:hello@requesta.app">hello@requesta.app</a>.</li>
          </ul>
        </section>

        <section id="rqpv-children" className="rqpv-section">
          <div className="rqpv-h2">9. Children's Privacy</div>
          <div className="rqpv-text">
            The App is not directed to children under 13 (or under 16 in jurisdictions where a higher minimum age applies).
            We do not knowingly collect personal data from children.
          </div>
        </section>

        <section id="rqpv-changes" className="rqpv-section">
          <div className="rqpv-h2">10. Updates to This Policy</div>
          <div className="rqpv-text">
            We may update this Privacy Policy. Changes will be posted on our website with a revised "Effective Date."
          </div>
        </section>

        <div className="rqpv-updated">Effective Date: November 3, 2026</div>
      </div>
    </main>
    </div>
  );
};

export default PrivacyPolicy;
